import React, { useState, useEffect, useRef } from 'react';
import { Mail, Send, Heart, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import GlitchText from './GlitchText';

const services = [
  { id: 'lyrics', name: 'BUY RHY LYRICS', available: true },
  { id: 'open-verse', name: 'BUY RHY OPEN VERSE', available: true },
  { id: 'feature', name: 'BUY RHY FEATURE', available: true },
  { id: 'placement', name: 'BUY RHY PLACEMENT', available: true },
  { id: 'ep-mixtape', name: 'BUY RHY EP OR MIXTAPE', available: true },
  { id: 'album', name: 'BUY RHY ALBUM', available: true },
  { id: 'live-performance', name: 'BUY RHY LIVE PERFORMANCE', available: true },
  { id: 'singing-lessons', name: 'BUY RHY SINGING LESSONS', available: true },
  { id: 'mixes', name: 'BUY RHY MIXES', available: true },
  { id: 'cover-art', name: 'BUY RHY COVER ART', available: true },
  { id: 'beats', name: 'BUY RHY BEATS', available: false, comingSoon: true },
];

// Glitch Title Component with TV Static Effect
const GlitchTitle = ({ children }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [staticOffset, setStaticOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!isHovered) return;
    
    let animationId;
    let time = 0;
    
    const animateStatic = () => {
      time += 0.05;
      const staticX = Math.sin(time * 3) * 2 + Math.sin(time * 7) * 0.5;
      const staticY = Math.cos(time * 2) * 1 + Math.cos(time * 5) * 0.3;
      setStaticOffset({ x: staticX, y: staticY });
      animationId = requestAnimationFrame(animateStatic);
    };
    
    animateStatic();
    return () => { if (animationId) cancelAnimationFrame(animationId); };
  }, [isHovered]);

  return (
    <h3 
      className="font-heading text-3xl md:text-4xl font-bold italic text-white text-center mb-12 cursor-pointer relative select-none tracking-wider"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setStaticOffset({ x: 0, y: 0 }); }}
      style={{
        color: isHovered ? 'transparent' : '#ffffff',
        background: isHovered 
          ? 'linear-gradient(135deg, #fff 0%, #a78bfa 20%, #f472b6 40%, #60a5fa 60%, #34d399 80%, #fff 100%)'
          : 'none',
        backgroundSize: '400% 400%',
        WebkitBackgroundClip: isHovered ? 'text' : 'unset',
        backgroundClip: isHovered ? 'text' : 'unset',
        animation: isHovered ? 'gradientFlow 4s ease infinite' : 'none',
      }}
    >
      {children}
      
      {/* TV Static RGB Layers */}
      {isHovered && (
        <>
          <span 
            className="absolute inset-0 pointer-events-none mix-blend-screen"
            style={{
              color: 'rgba(255, 80, 80, 0.7)',
              transform: `translate(${staticOffset.x + 2}px, ${staticOffset.y}px)`,
              filter: 'blur(0.5px)',
            }}
            aria-hidden="true"
          >
            {children}
          </span>
          <span 
            className="absolute inset-0 pointer-events-none mix-blend-screen"
            style={{
              color: 'rgba(80, 255, 120, 0.6)',
              transform: `translate(${-staticOffset.x * 0.7}px, ${staticOffset.y * 1.2}px)`,
              filter: 'blur(0.3px)',
            }}
            aria-hidden="true"
          >
            {children}
          </span>
          <span 
            className="absolute inset-0 pointer-events-none mix-blend-screen"
            style={{
              color: 'rgba(80, 150, 255, 0.7)',
              transform: `translate(${staticOffset.x * 0.5}px, ${-staticOffset.y * 0.8}px)`,
              filter: 'blur(0.4px)',
            }}
            aria-hidden="true"
          >
            {children}
          </span>
        </>
      )}

      <style>{`
        @keyframes gradientFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </h3>
  );
};

const EmailSignup = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorkExpanded, setIsWorkExpanded] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        setEmail('');
      }
    } catch (error) {
      setIsSubmitted(true);
      setEmail('');
    }
    
    setIsLoading(false);
  };

  const handleServiceClick = (service) => {
    if (service.available) {
      navigate(`/prices/${service.id}`);
    }
  };

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24" data-testid="email-signup-section">
      <div className="max-w-4xl mx-auto">
        {/* Email Signup */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 border border-white/10 mb-8">
            <Mail className="w-7 h-7 text-white/60" />
          </div>

          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4 tracking-wider">
            <GlitchText>STAY UPDATED</GlitchText>
          </h2>
          <p className="font-mono text-sm md:text-base text-white/50 mb-10 max-w-md mx-auto">
            <GlitchText>Be the first to know about new releases, exclusive content, and upcoming shows.</GlitchText>
          </p>

          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <div className="flex-1 relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 text-white placeholder-white/30 font-mono text-sm focus:outline-none focus:border-white/30 transition-colors"
                  data-testid="email-input"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="px-8 py-4 bg-white text-black font-accent text-sm uppercase tracking-[0.15em] hover:bg-white/90 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="subscribe-btn"
              >
                {isLoading ? (
                  <span className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    Subscribe
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="flex items-center justify-center gap-3 text-white/80 font-mono animate-fade-in">
              <Heart className="w-5 h-5 text-red-400" />
              <span><GlitchText>You&apos;re on the list!</GlitchText></span>
            </div>
          )}
        </div>

        {/* WHAT YOU CAN DO Section */}
        <div className="pt-12 border-t border-white/10">
          <GlitchTitle>WHAT YOU CAN DO</GlitchTitle>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Work With Me Box */}
            <div className="bg-white/5 border border-white/10 p-6 hover:border-white/20 transition-all duration-300">
              <button
                onClick={() => setIsWorkExpanded(!isWorkExpanded)}
                className="w-full flex items-center justify-between text-left"
                data-testid="work-with-me-btn"
              >
                <span className="font-heading text-xl font-bold text-white tracking-wider"><GlitchText>WORK WITH ME</GlitchText></span>
                {isWorkExpanded ? (
                  <ChevronUp className="w-5 h-5 text-white/60" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white/60" />
                )}
              </button>

              <div className={`mt-4 space-y-1 overflow-hidden transition-all duration-500 ${isWorkExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => handleServiceClick(service)}
                    disabled={!service.available}
                    className={`w-full text-left px-4 py-3 transition-all duration-300 ${
                      service.comingSoon
                        ? 'cursor-not-allowed'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid={`service-${service.id}`}
                  >
                    {service.comingSoon ? (
                      <span className="flex items-center gap-2">
                        <span className="text-white/30 text-xs font-mono">coming soon</span>
                        <span className="text-red-500 font-accent text-sm tracking-wider">{service.name}</span>
                      </span>
                    ) : (
                      <span className="font-mono text-xs tracking-wider">{service.name}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Email Box */}
            <div className="bg-white/5 border border-white/10 p-6 hover:border-white/20 transition-all duration-300 flex flex-col justify-center">
              <h4 className="font-heading text-xl font-bold text-white mb-4 tracking-wider"><GlitchText>CONTACT</GlitchText></h4>
              <a
                href="mailto:rhy.inquiries@gmail.com"
                className="group flex items-center gap-3 text-white/70 hover:text-white transition-colors"
                data-testid="contact-email"
              >
                <Mail className="w-5 h-5" />
                <span className="font-mono text-sm">rhy.inquiries@gmail.com</span>
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>

          {/* Support The Movement */}
          <div className="mt-12 text-center">
            <h4 className="font-heading text-xl md:text-2xl font-bold text-white mb-6 tracking-wider">
              <GlitchText>SUPPORT THE MOVEMENT</GlitchText>
            </h4>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://paypal.me/justice11259"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/10 hover:border-white/30 hover:bg-white/10 transition-all duration-300"
                data-testid="paypal-link"
              >
                <svg className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.629h6.622c2.203 0 3.817.562 4.797 1.672.877.994 1.248 2.336 1.103 3.988-.016.174-.039.36-.067.555l-.002.009v.171c-.342 2.16-1.427 3.597-3.018 4.456-.883.477-1.89.74-2.988.825-.259.02-.527.03-.8.03H9.315a.77.77 0 0 0-.758.628l-.934 5.912z"/>
                </svg>
                <span className="font-mono text-sm text-white/70 group-hover:text-white transition-colors">PayPal</span>
                <ExternalLink className="w-3 h-3 text-white/40 group-hover:text-white/70 transition-colors" />
              </a>

              <a
                href="https://cash.app/$justice112"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/10 hover:border-white/30 hover:bg-white/10 transition-all duration-300"
                data-testid="cashapp-link"
              >
                <svg className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.59 3.47A5.1 5.1 0 0 0 20.54.42 5.05 5.05 0 0 0 17.5 0h-11A5.07 5.07 0 0 0 .42 3.47 5 5 0 0 0 0 6.5v11a5.07 5.07 0 0 0 3.47 5.08 5 5 0 0 0 3 .42h11a5.07 5.07 0 0 0 5.08-3.47 5 5 0 0 0 .42-3v-11a5.05 5.05 0 0 0-.38-3.06zm-6.1 5.87l-.8.8a.55.55 0 0 1-.77 0l-1.94-1.93-1.93 1.93a.55.55 0 0 1-.78 0l-.8-.8a.55.55 0 0 1 0-.77l1.94-1.94-1.94-1.93a.55.55 0 0 1 0-.78l.8-.8a.55.55 0 0 1 .78 0l1.93 1.94 1.94-1.94a.55.55 0 0 1 .77 0l.8.8a.55.55 0 0 1 0 .78l-1.93 1.93 1.93 1.94a.55.55 0 0 1 0 .77z"/>
                </svg>
                <span className="font-mono text-sm text-white/70 group-hover:text-white transition-colors">$justice112</span>
                <ExternalLink className="w-3 h-3 text-white/40 group-hover:text-white/70 transition-colors" />
              </a>
            </div>

            <p className="mt-8 font-accent text-xs text-white/30 tracking-[0.3em] uppercase">
              <GlitchText>Support The Revolution</GlitchText>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmailSignup;
